import { useState, useEffect, useCallback, useRef } from "react";
import { ingestionLogsApi } from "../api/ingestionLogs";

export type IngestionStatus = "not_started" | "pending" | "completed" | "failed";

interface UseIngestionStatusResult {
  status: IngestionStatus;
  errorMessage: string | null;
}

export function useIngestionStatus(enabled: boolean = true): UseIngestionStatusResult {
  const [status, setStatus] = useState<IngestionStatus>("not_started");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async () => {
    try {
      const logs = await ingestionLogsApi.getInitialPull();
      if (logs.length === 0) {
        setStatus("not_started");
        return;
      }

      const latest = logs[0];
      setStatus(latest.status);
      setErrorMessage(latest.errorMessage);
    } catch {
      // Silently ignore polling errors — will retry on next interval
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    poll();

    intervalRef.current = setInterval(() => {
      poll();
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, poll]);

  // Stop polling once we reach a terminal state
  useEffect(() => {
    if (status === "completed" || status === "failed") {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [status]);

  return { status, errorMessage };
}
